import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Target, 
  TrendingUp, 
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  DollarSign,
  ArrowUp,
  Flame
} from 'lucide-react';

interface DailyTarget {
  date: string;
  original_target: number;
  carryover_amount: number;
  adjusted_target: number;
  achieved_amount: number;
  shortfall: number;
  completion_percentage: number;
  is_critical: boolean;
  days_behind: number;
}

interface CarryoverSummary {
  current_target: DailyTarget;
  accumulated_shortfall: number;
  days_with_shortfall: number;
  total_target_with_carryover: number;
  urgency_level: 'low' | 'medium' | 'high' | 'critical';
}

export function AutomaticCarryoverKPI() {
  const { user, selectedCompany } = useAuth();
  const [carryoverData, setCarryoverData] = useState<CarryoverSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const calculateCarryoverData = useCallback(async () => {
    if (!selectedCompany?.id || !user?.id) return;

    try {
      setLoading(true);

      // 🔍 VALIDACIÓN 1: Verificar que el usuario tenga metas asignadas
      const { data: assignments, error: assignmentsError } = await supabase
        .from('target_user_assignments')
        .select('*')
        .eq('user_id', user.id);

      if (assignmentsError) throw assignmentsError;

      if (!assignments || assignments.length === 0) {
        console.log('❌ VALIDACIÓN: Usuario sin metas asignadas');
        setCarryoverData(null);
        return;
      }

      const targetIds = assignments.map(a => a.target_id);

      // 🔍 VALIDACIÓN 2: Verificar que existan metas activas válidas para la empresa
      const { data: targets, error: targetsError } = await supabase
        .from('sales_targets_with_carryover')
        .select('*')
        .in('id', targetIds)
        .eq('company_id', selectedCompany.id)
        .in('status', ['active', 'carried_over']);

      if (targetsError) throw targetsError;

      if (!targets || targets.length === 0) {
        console.log('❌ VALIDACIÓN: No hay metas activas válidas para la empresa');
        setCarryoverData(null);
        return;
      }

      // 🔍 VALIDACIÓN 3: Verificar que la meta tenga configuración válida
      const target = targets[0];
      const assignment = assignments.find(a => a.target_id === target.id);
      const userPercentage = assignment?.assigned_percentage || 0;

      if (userPercentage <= 0) {
        console.log('❌ VALIDACIÓN: Porcentaje de asignación inválido');
        setCarryoverData(null);
        return;
      }

      // 🔍 VALIDACIÓN 4: Verificar período válido
      const endDate = new Date(target.period_end_date);
      const startDate = new Date(target.period_start_date);
      const today = new Date();

      if (startDate > today) {
        console.log('❌ VALIDACIÓN: Meta aún no ha iniciado');
        setCarryoverData(null);
        return;
      }

      console.log('✅ VALIDACIONES EXITOSAS: Meta válida encontrada', {
        targetId: target.id,
        targetName: target.target_name,
        userPercentage,
        status: target.status,
        period: `${target.period_start_date} - ${target.period_end_date}`,
        monthlyTarget: target.adjusted_target_amount || target.original_target_amount,
        userMonthlyTarget: (target.adjusted_target_amount || target.original_target_amount) * (userPercentage / 100)
      });

      // Obtener el progreso diario de los últimos 30 días
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: dailyProgress, error: progressError } = await supabase
        .from('daily_sales_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('target_id', target.id)
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (progressError) throw progressError;

      // Calcular el arrastre acumulado
      let accumulatedShortfall = 0;
      let daysWithShortfall = 0;
      const todayString = new Date().toISOString().split('T')[0];

      // Recorrer días anteriores para calcular faltantes
      if (dailyProgress) {
        for (const day of dailyProgress) {
          if (day.date >= todayString) continue; // Solo días pasados
          
          const dayShortfall = Math.max(0, day.daily_target_amount - day.daily_sales_amount);
          if (dayShortfall > 0) {
            accumulatedShortfall += dayShortfall;
            daysWithShortfall++;
          }
        }
      }

      // Datos del día actual
      const todaysProgress = dailyProgress?.find(d => d.date === todayString);
      
      // Calcular días laborales totales del período
      const totalWorkingDays = calculateTotalWorkingDays(
        new Date(target.period_start_date), 
        new Date(target.period_end_date)
      );
      
      // Meta DIARIA base del usuario (meta mensual / días laborales)
      const userTotalTarget = (target.adjusted_target_amount || target.original_target_amount) * (userPercentage / 100);
      const baseDailyTarget = totalWorkingDays > 0 ? userTotalTarget / totalWorkingDays : 0;
      
      // Calcular días laborales restantes (incluyendo hoy)
      const remainingWorkingDays = calculateRemainingWorkingDays(target.period_end_date);
      
      // Arrastre distribuido para HOY
      const carryoverForToday = remainingWorkingDays > 0 
        ? accumulatedShortfall / remainingWorkingDays 
        : accumulatedShortfall;
      
      // Meta ajustada de HOY = meta base diaria + arrastre del día
      const adjustedDailyTarget = baseDailyTarget + carryoverForToday;

      console.log('📊 CÁLCULO META DIARIA:', {
        userTotalTarget,
        totalWorkingDays,
        baseDailyTarget,
        accumulatedShortfall,
        remainingWorkingDays,
        carryoverForToday,
        adjustedDailyTarget,
        achievedToday: todaysProgress?.daily_sales_amount || 0
      });

      const currentTarget: DailyTarget = {
        date: todayString,
        original_target: baseDailyTarget,
        carryover_amount: carryoverForToday,
        adjusted_target: adjustedDailyTarget,
        achieved_amount: todaysProgress?.daily_sales_amount || 0,
        shortfall: Math.max(0, adjustedDailyTarget - (todaysProgress?.daily_sales_amount || 0)),
        completion_percentage: adjustedDailyTarget > 0 
          ? ((todaysProgress?.daily_sales_amount || 0) / adjustedDailyTarget) * 100 
          : 0,
        is_critical: daysWithShortfall >= 3,
        days_behind: daysWithShortfall
      };

      // Determinar nivel de urgencia
      let urgencyLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
      if (daysWithShortfall >= 5) urgencyLevel = 'critical';
      else if (daysWithShortfall >= 3) urgencyLevel = 'high';
      else if (daysWithShortfall >= 1) urgencyLevel = 'medium';

      const summary: CarryoverSummary = {
        current_target: currentTarget,
        accumulated_shortfall: accumulatedShortfall,
        days_with_shortfall: daysWithShortfall,
        total_target_with_carryover: adjustedDailyTarget,
        urgency_level: urgencyLevel
      };

      // Guardar también la meta mensual para mostrarla
      (summary as any).monthly_target = userTotalTarget;
      (summary as any).monthly_achieved = 0; // Se calculará desde daily_progress

      // Calcular total vendido en el mes
      if (dailyProgress && dailyProgress.length > 0) {
        const totalSales = dailyProgress.reduce((sum, day) => {
          return sum + (day.daily_sales_amount || 0);
        }, 0);
        (summary as any).monthly_achieved = totalSales;
      }

      setCarryoverData(summary);

    } catch (error) {
      console.error('Error calculating carryover data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.id, user?.id]);

  const calculateTotalWorkingDays = (startDate: Date, endDate: Date) => {
    let workingDays = 0;
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      // Lunes a Viernes
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return workingDays;
  };

  const calculateRemainingWorkingDays = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (end < today) return 0;
    
    let workingDays = 0;
    const current = new Date(today);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // Lunes a Viernes
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return workingDays;
  };

  useEffect(() => {
    calculateCarryoverData();
    
    // Actualizar cada minuto para tiempo real
    const interval = setInterval(calculateCarryoverData, 60 * 1000);
    
    // Configurar actualizaciones en tiempo real
    const channel = supabase
      .channel('carryover-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_sales_progress'
        },
        () => calculateCarryoverData()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales'
        },
        () => calculateCarryoverData()
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [calculateCarryoverData]);

  const { formatCurrency } = useTenantConfig();

  const getUrgencyConfig = (level: string) => {
    switch (level) {
      case 'critical':
        return {
          color: 'text-status-error',
          bgColor: 'bg-status-error-bg',
          icon: Flame,
          text: 'CRÍTICO',
          description: 'Situación extrema - Requiere acción inmediata'
        };
      case 'high':
        return {
          color: 'text-warning',
          bgColor: 'bg-status-warning-bg',
          icon: AlertTriangle,
          text: 'URGENTE',
          description: 'Varios días de retraso - Atención prioritaria'
        };
      case 'medium':
        return {
          color: 'text-warning',
          bgColor: 'bg-status-warning-bg',
          icon: Clock,
          text: 'ALERTA',
          description: 'Necesita recuperación - Enfoque requerido'
        };
      default:
        return {
          color: 'text-status-success',
          bgColor: 'bg-status-success-bg',
          icon: CheckCircle,
          text: 'NORMAL',
          description: 'Sin arrastre pendiente'
        };
    }
  };

  if (loading) {
    return (
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <div className="animate-pulse">
            <div className="h-6 bg-muted/50 rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted/50 rounded w-32"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-muted/50 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!carryoverData) {
    return (
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg font-semibold">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
              <Target className="h-4 w-4 text-primary" />
            </div>
            Mi Meta Diaria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">
              No tienes metas asignadas actualmente
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const urgencyConfig = getUrgencyConfig(carryoverData.urgency_level);
  const UrgencyIcon = urgencyConfig.icon;

  return (
    <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg font-semibold">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
            <Target className="h-4 w-4 text-primary" />
          </div>
          Mi Meta de Hoy
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground font-medium flex items-center space-x-2">
          <Calendar className="h-3 w-3" />
          <span>{new Date().toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          <span className="text-xs">• Arrastre automático</span>
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Urgency Alert */}
        {carryoverData.days_with_shortfall > 0 && (
          <Alert className={`border-l-4 ${urgencyConfig.color.replace('text-', 'border-l-')} ${urgencyConfig.bgColor}`}>
            <UrgencyIcon className={`h-4 w-4 ${urgencyConfig.color}`} />
            <AlertDescription className="font-medium">
              <div className="flex items-center justify-between">
                <span>{urgencyConfig.text}: {carryoverData.days_with_shortfall} días de retraso</span>
                <Badge variant="destructive" className="text-xs">
                  {formatCurrency(carryoverData.accumulated_shortfall)} acumulado
                </Badge>
              </div>
              <p className="text-xs mt-1 opacity-80">{urgencyConfig.description}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Monthly Target Overview */}
        {(carryoverData as any).monthly_target && (
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 backdrop-blur-sm rounded-2xl p-4 border border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                Meta del Mes
              </p>
              <Badge variant="outline" className="text-xs">
                {((carryoverData as any).monthly_achieved / (carryoverData as any).monthly_target * 100).toFixed(1)}%
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-foreground">
                  {formatCurrency((carryoverData as any).monthly_achieved)}
                </p>
                <p className="text-xs text-muted-foreground">
                  de {formatCurrency((carryoverData as any).monthly_target)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-status-error">
                  Falta: {formatCurrency((carryoverData as any).monthly_target - (carryoverData as any).monthly_achieved)}
                </p>
              </div>
            </div>
            <div className="mt-2">
              <Progress 
                value={Math.min(100, (carryoverData as any).monthly_achieved / (carryoverData as any).monthly_target * 100)} 
                className="h-2"
              />
            </div>
          </div>
        )}

        {/* Today's Target */}
        <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-4 border border-border/20">
          <div className="mb-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Meta de Hoy</p>
          </div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(carryoverData.current_target.achieved_amount)}
              </p>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  de {formatCurrency(carryoverData.current_target.adjusted_target)}
                </p>
                {carryoverData.current_target.carryover_amount > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground">
                      Meta base: {formatCurrency(carryoverData.current_target.original_target)}
                    </span>
                    <ArrowUp className="h-3 w-3 text-status-error" />
                    <span className="text-xs text-status-error font-medium">
                      + Arrastre: {formatCurrency(carryoverData.current_target.carryover_amount)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <Badge 
                variant={carryoverData.current_target.completion_percentage >= 100 ? 'default' : 'secondary'} 
                className="text-xs"
              >
                {carryoverData.current_target.completion_percentage.toFixed(1)}%
              </Badge>
              {carryoverData.current_target.shortfall > 0 && (
                <Badge variant="destructive" className="text-xs">
                  Falta: {formatCurrency(carryoverData.current_target.shortfall)}
                </Badge>
              )}
            </div>
          </div>

          <Progress 
            value={Math.min(100, carryoverData.current_target.completion_percentage)} 
            className="h-3 mb-2"
          />

          {carryoverData.accumulated_shortfall > 0 && (
            <div className="text-xs text-center p-2 bg-status-error-bg border border-status-error-border rounded-lg">
              <span className="text-status-error font-medium">
                ⚠️ Arrastre acumulado de {carryoverData.days_with_shortfall} días por {formatCurrency(carryoverData.accumulated_shortfall)}
              </span>
            </div>
          )}
        </div>

        {/* Critical Business Impact */}
        {carryoverData.urgency_level === 'critical' && (
          <div className="bg-gradient-to-r from-status-error-bg to-status-warning-bg border border-status-error-border rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Flame className="h-5 w-5 text-status-error" />
              <span className="font-bold text-status-error">IMPACTO CRÍTICO EN EL NEGOCIO</span>
            </div>
            <p className="text-sm text-status-error">
              El retraso acumulado de <strong>{carryoverData.days_with_shortfall} días</strong> está 
              afectando seriamente tu desempeño y el rendimiento general de la empresa. 
              <strong> Acción inmediata requerida</strong>.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}